import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qFormInstanceById,
  qFormInstanceValues,
  qFormInstanceSignatures,
  qFormDefinitionById,
  qFormTemplatePages,
  qFormTemplateMappings,
  qPatientById,
  qPatientContact,
  qEncounterById,
  parseFieldsSchemaV1,
  flattenFields,
  type FormInstanceListRow,
  type FormInstanceValueRow,
  type FormInstanceSignatureRow,
  type FormDefinitionRow,
  type FormTemplatePageRow,
  type FormTemplateMappingRow,
  type FormFieldsSchemaV1,
  type PatientDetailRow,
  type PatientContactRow,
  type PatientEncounterRow,
  orchestrateTwoWaySave,
  buildUpdateSql,
} from '@helios/data-access';
import { SectionRenderer, TemplateOverlayView, ModeSwitch } from './FormFieldRenderers';

// ─── Helpers ───

// Components (SignaturePad, SectionRenderer, TemplateOverlayView, ModeSwitch, etc.)
// are imported from ./FormFieldRenderers — shared across encounter forms, registration, and grouped edits.

// ─── Main Renderer Page ───

export function FormRendererPage() {
  const {
    id: patientId,
    encounterId,
    formInstanceId,
  } = useParams<{
    id: string;
    encounterId: string;
    formInstanceId: string;
  }>();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();

  // ── Data ──
  const { data: instanceRows = [] } = usePowerSyncQuery<FormInstanceListRow>(
    qFormInstanceById(formInstanceId ?? ''),
  );
  const instance = instanceRows[0] ?? null;

  const { data: defRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qFormDefinitionById(instance?.form_definition_id ?? ''),
  );
  const definition = defRows[0] ?? null;

  const { data: valueRows = [] } = usePowerSyncQuery<FormInstanceValueRow>(
    qFormInstanceValues(formInstanceId ?? ''),
  );

  const { data: sigRows = [] } = usePowerSyncQuery<FormInstanceSignatureRow>(
    qFormInstanceSignatures(formInstanceId ?? ''),
  );

  // ── Derived ──
  const schema = useMemo<FormFieldsSchemaV1>(() => {
    if (!definition) return { version: 1, sections: [] };
    return parseFieldsSchemaV1(definition.fields_schema);
  }, [definition?.id, definition?.fields_schema]);

  const valuesMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of valueRows) {
      if (v.value !== null) map[v.field_key] = v.value;
    }
    return map;
  }, [valueRows]);

  const sigMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sigRows) {
      map[s.field_key] = s.stroke_data;
    }
    return map;
  }, [sigRows]);

  // Build a map of valueRow IDs for upsert
  const valueIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of valueRows) map[v.field_key] = v.id;
    return map;
  }, [valueRows]);

  const sigIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sigRows) map[s.field_key] = s.id;
    return map;
  }, [sigRows]);

  const isCompleted = instance?.status === 'completed';

  // ── View mode ──
  const defaultView = instance?.default_view === 'render' ? 'render' : 'form';
  const [viewMode, setViewMode] = useState<'form' | 'render'>(defaultView);
  // Override to render for completed forms
  useEffect(() => {
    if (isCompleted) setViewMode('render');
  }, [isCompleted]);
  // Sync default_view when instance loads
  useEffect(() => {
    if (instance && !isCompleted)
      setViewMode(instance.default_view === 'render' ? 'render' : 'form');
  }, [instance?.id]);

  // ── Template data (for render mode) ──
  const { data: templatePages = [] } = usePowerSyncQuery<FormTemplatePageRow>(
    qFormTemplatePages(instance?.form_definition_id ?? ''),
  );
  const { data: templateMappings = [] } = usePowerSyncQuery<FormTemplateMappingRow>(
    qFormTemplateMappings(instance?.form_definition_id ?? ''),
  );
  const allFields = useMemo(() => flattenFields(schema), [schema]);

  // ── Patient + Encounter data (for binding resolution) ──
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    qPatientById(patientId ?? ''),
  );
  const patient = patientRows[0] ?? null;

  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId ?? ''),
  );
  const contact = contactRows[0] ?? null;

  const { data: encounterDetailRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const encounterDetail = encounterDetailRows[0] ?? null;

  // Build binding resolution map from live data
  const bindingData = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (patient) {
      if (patient.full_name) map['patient.full_name'] = patient.full_name;
      if (patient.birth_date) map['patient.birth_date'] = patient.birth_date;
      if (patient.sex) map['patient.sex'] = patient.sex;
      if (patient.patient_number) map['patient.patient_number'] = patient.patient_number;
      if (patient.blood_group_abo) map['patient.blood_group_abo'] = patient.blood_group_abo;
      if (patient.drug_allergies_notes)
        map['patient.drug_allergies_notes'] = patient.drug_allergies_notes;
      if (patient.medical_notes) map['patient.medical_notes'] = patient.medical_notes;
    }
    if (contact) {
      if (contact.email) map['patient.email'] = contact.email;
      if (contact.mobile_number) {
        const code = contact.mobile_country_code ? `${contact.mobile_country_code} ` : '';
        map['patient.mobile_number'] = `${code}${contact.mobile_number}`;
      }
    }
    if (encounterDetail) {
      if (encounterDetail.type) map['encounter.type'] = encounterDetail.type;
      if (encounterDetail.started_at) map['encounter.started_at'] = encounterDetail.started_at;
      if (encounterDetail.provider_name)
        map['encounter.provider_name'] = encounterDetail.provider_name;
    }
    // form_context: resolved from runtime environment (safe no-ops for unavailable values)
    map['form_context.current_date'] = new Date().toISOString().slice(0, 10);
    // form_context.clinic_name — not yet available in this component; omitted safely
    return map;
  }, [patient, contact, encounterDetail]);

  // ── Handlers ──
  const handleValueChange = useCallback(
    async (fieldKey: string, val: string) => {
      if (!db || !formInstanceId || isCompleted) return;
      const existingId = valueIdMap[fieldKey];
      if (existingId) {
        await db.execute(
          `UPDATE form_instance_values SET value = ?, updated_at = datetime('now') WHERE id = ?`,
          [val, existingId],
        );
      } else {
        await db.execute(
          `INSERT INTO form_instance_values (id, form_instance_id, field_key, value, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [crypto.randomUUID(), formInstanceId, fieldKey, val],
        );
      }
    },
    [db, formInstanceId, valueIdMap, isCompleted],
  );

  const handleSignatureSave = useCallback(
    async (fieldKey: string, _dataUrl: string, strokeData: string) => {
      if (!db || !formInstanceId || isCompleted) return;
      const existingId = sigIdMap[fieldKey];
      if (existingId) {
        await db.execute(
          `UPDATE form_instance_signatures SET stroke_data = ?, updated_at = datetime('now') WHERE id = ?`,
          [strokeData, existingId],
        );
      } else {
        await db.execute(
          `INSERT INTO form_instance_signatures (id, form_instance_id, field_key, stroke_data, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [crypto.randomUUID(), formInstanceId, fieldKey, strokeData],
        );
      }
    },
    [db, formInstanceId, sigIdMap, isCompleted],
  );

  const handleComplete = useCallback(async () => {
    if (!db || !formInstanceId) return;

    // ── Orchestrate two-way bound field writes ──
    const result = orchestrateTwoWaySave(schema, valuesMap);
    for (const op of result.operations) {
      const { sql, params } = buildUpdateSql(op);
      if (op.table === 'patients' && patientId) {
        await db.execute(`${sql} WHERE id = ?`, [...params, patientId]);
      } else if (op.table === 'patient_contacts' && contact?.id) {
        await db.execute(`${sql} WHERE id = ?`, [...params, contact.id]);
      } else if (op.table === 'patient_encounters' && encounterId) {
        await db.execute(`${sql} WHERE id = ?`, [...params, encounterId]);
      }
      // Unsupported tables are safely skipped
    }

    // ── Multi-row operations (e.g. patient_allergies) ──
    if (patientId) {
      for (const mop of result.multiRowOps) {
        // Soft-delete existing rows for this type
        await db.execute(
          `UPDATE ${mop.table} SET deleted_at = datetime('now') WHERE patient_id = ? AND ${mop.filterColumn} = ? AND deleted_at IS NULL`,
          [patientId, mop.filterValue],
        );
        // Insert new rows
        for (const val of mop.values) {
          const staticEntries = Object.entries(mop.staticColumns);
          const cols = [
            'id',
            'patient_id',
            mop.valueColumn,
            ...staticEntries.map(([k]) => k),
            'updated_at',
          ];
          const placeholders = cols.map((c) => (c === 'updated_at' ? "datetime('now')" : '?'));
          const params = [crypto.randomUUID(), patientId, val, ...staticEntries.map(([, v]) => v)];
          await db.execute(
            `INSERT INTO ${mop.table} (${cols.join(', ')}) VALUES (${placeholders.join(', ')})`,
            params,
          );
        }
      }
    }

    // ── Mark form as completed ──
    await db.execute(
      `UPDATE form_instances SET status = 'completed', completed_at = datetime('now'), updated_at = datetime('now') WHERE id = ?`,
      [formInstanceId],
    );
  }, [db, formInstanceId, schema, valuesMap, patientId, encounterId, contact?.id]);

  const handleCreateNewDraft = useCallback(async () => {
    if (!db || !instance) return;
    const newId = crypto.randomUUID();
    // Duplicate instance as draft
    await db.execute(
      `INSERT INTO form_instances (id, org_id, clinic_id, form_definition_id, patient_id, encounter_id, appointment_id, queue_id, visit_id, status, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 'draft', datetime('now'), datetime('now'))`,
      [
        newId,
        instance.org_id,
        instance.clinic_id,
        instance.form_definition_id,
        instance.patient_id,
        instance.encounter_id,
        instance.appointment_id,
        instance.queue_id,
        instance.visit_id,
      ],
    );
    // Duplicate values
    for (const v of valueRows) {
      await db.execute(
        `INSERT INTO form_instance_values (id, form_instance_id, field_key, value, created_at, updated_at) VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [crypto.randomUUID(), newId, v.field_key, v.value],
      );
    }
    // Duplicate signatures
    for (const s of sigRows) {
      await db.execute(
        `INSERT INTO form_instance_signatures (id, form_instance_id, field_key, stroke_data, image_url, created_at, updated_at) VALUES (?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [crypto.randomUUID(), newId, s.field_key, s.stroke_data, s.image_url],
      );
    }
    navigate(`/patient/${patientId}/encounters/${encounterId}/forms/${newId}`);
  }, [db, instance, valueRows, sigRows, patientId, encounterId, navigate]);

  const backUrl = `/patient/${patientId}/encounters/${encounterId}?tab=forms`;

  if (!formInstanceId)
    return (
      <div style={{ padding: 'var(--sp-5)', color: 'var(--text-muted)' }}>No form selected.</div>
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={() => navigate(backUrl)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="chevron-left" />
          </button>
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              {instance?.form_name ?? 'Loading...'}
            </h1>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {isCompleted ? 'Completed' : 'Draft'} &middot; {definition?.name ?? ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ModeSwitch mode={viewMode} onChange={setViewMode} primary={primary} />
          {!isCompleted && (
            <button
              onClick={handleComplete}
              style={{
                height: 34,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="check-circle" /> Complete
            </button>
          )}
          {isCompleted && (
            <button
              onClick={handleCreateNewDraft}
              style={{
                height: 34,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="plus" /> New Draft from This
            </button>
          )}
        </div>
      </div>

      {/* Completed banner */}
      {isCompleted && (
        <div
          style={{
            margin: 'var(--sp-4) var(--sp-5) 0',
            padding: 'var(--sp-3) var(--sp-4)',
            background: 'var(--color-success-bg)',
            border: '1px solid var(--color-success-border, #bbf7d0)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-success-text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <NavIcon name="check-circle" /> This form has been completed and is now read-only.
        </div>
      )}

      {/* Content — Form mode or Render mode */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {viewMode === 'form' ? (
          <div style={{ padding: 'var(--sp-5)' }}>
            <div
              style={{
                maxWidth: 720,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-5)',
              }}
            >
              {schema.sections.map((section) => (
                <SectionRenderer
                  key={section.id}
                  section={section}
                  values={valuesMap}
                  signatures={sigMap}
                  onValueChange={handleValueChange}
                  onSignatureSave={handleSignatureSave}
                  readOnly={isCompleted}
                  bindingData={bindingData}
                />
              ))}
              {schema.sections.length === 0 && (
                <div
                  style={{
                    padding: 'var(--sp-8)',
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {definition ? 'This form has no sections or fields defined.' : 'Loading form...'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <TemplateOverlayView
            pages={templatePages}
            mappings={templateMappings}
            fields={allFields}
            values={valuesMap}
            signatures={sigMap}
            schema={schema}
            bindingData={bindingData}
            onValueChange={handleValueChange}
            onSignatureSave={handleSignatureSave}
            readOnly={isCompleted}
          />
        )}
      </div>
    </div>
  );
}
